/*
 * @Version: 1.0
 * @Author: admin
 * @Description: request
 * @Date: 2022-06-23 14:44:40
 * @LastEditors: sky
 * @LastEditTime: 2022-09-15 11:48:04
 */
import axios, {AxiosInstance, AxiosRequestHeaders, AxiosError, AxiosResponse} from "axios";
import {
    APISchema,
    RequestPath,
    RequestFunction,
    RequestOptions,
    CreateRequestConfig,
    CreateRequestClient,
} from "@/types/response";
import {Message} from "@arco-design/web-vue";
import router from "@/router";
import useUser from "@/hooks/user";
import {useAppStore} from "@/store";

const MATCH_METHOD = /^(GET|POST|PUT|DELETE|HEAD|OPTIONS|CONNECT|TRACE|PATCH)\s+/;
const MATCH_PATH_PARAMS = /:(\w+)/g;
const USE_DATA_METHODS = ["POST", "PUT", "PATCH", "DELETE"];
import {getToken, clearToken} from "@/utils/auth";

interface MyResponseType<T = any> {
    msg: string;
    data?: T;
    rows?: T;
    code: number;
}

function attachAPI<T extends APISchema>(
    client: AxiosInstance,
    apis: CreateRequestConfig<T>["apis"]
): CreateRequestClient<T> {
    const hostApi: CreateRequestClient<T> = Object.create(null);
    for (const apiName in apis) {
        const apiConfig = apis[apiName];
        // 配置为一个函数
        if (typeof apiConfig === "function") {
            hostApi[apiName] = apiConfig as RequestFunction;
            continue;
        }
        let apiOptions = {};
        let apiPath = apiConfig as RequestPath;
        // 配置为一个对象
        if (typeof apiConfig === "object") {
            const {path, ...rest} = apiConfig as RequestOptions;
            apiPath = path as RequestPath;
            apiOptions = rest;
        }
        hostApi[apiName] = (params, options) => {
            const _params = typeof params == "string" || Array.isArray(params) ? params : {...((params as object) || {})};
            // 匹配路径中请求方法，如：'POST /api/test'
            const [prefix, method] = apiPath.match(MATCH_METHOD) || ["GET ", "GET"];
            // 剔除掉 POST  前缀
            let url = apiPath.replace(prefix, "");
            // 匹配路径中的参数占位符， 如 '/api/:user_id/:res_id'
            const matchParams = apiPath.match(MATCH_PATH_PARAMS);
            if (matchParams) {
                matchParams.forEach((match) => {
                    const key = match.replace(":", "");
                    if (Reflect.has(_params, key)) {
                        url = url.replace(match, Reflect.get(_params, key));
                        Reflect.deleteProperty(_params, key);
                    }
                });
            }
            const requestParams = USE_DATA_METHODS.includes(method) ? {data: _params} : {params: _params};
            return client.request({
                url,
                method: method.toLowerCase(),
                ...requestParams,
                ...apiOptions,
                ...options,
            });
        };
    }
    return hostApi;
}

// 创建请求客户端
export function createRequestClient<T extends APISchema>(
    requestConfig: CreateRequestConfig<T>
): CreateRequestClient<T> {
    const client = axios.create({
        baseURL: requestConfig.baseURL,
        headers: requestConfig.headers,
        timeout: 600000,
    });
    // 附加各业务请求头
    client.interceptors.request.use((config: any) => {
        const token = getToken();
        if (!requestConfig.noToken) {
            if (token) {
                config.headers.Authorization = `Bearer ${token}`;
            }
        }
        const headerHandlers = (requestConfig.headerHandlers || []).map((handler) => {
            return handler(config)
                .then((mixHeaders: AxiosRequestHeaders) => {
                    Object.assign(config.headers, mixHeaders);
                })
                .catch();
        });
        return Promise.all(headerHandlers).then(() => config);
    });

    // 拦截请求
    client.interceptors.response.use(
        (response: AxiosResponse) => {
            if (response.status !== 200) {
                const reponseError = "response error";
                Message.error("服务器错误，请稍后重试");
                return Promise.reject(reponseError);
            } else {
                if (response.data.code == 401) {
                    const {logout} = useUser();
                    logout().then((res) => {
                        const appStore = useAppStore();
                        appStore.setHasRoutes(false);
                        router.replace({name: "login"});
                        Message.error("登录超时，请重新登录");
                    });
                    return Promise.reject(false);
                } else {
                    return response.data;
                }
            }
        },
        (error: AxiosError) => {
            const requestError = requestConfig.errorHandler ? requestConfig.errorHandler(error) : error;
            Message.error("连接错误，请稍后重试");
            return Promise.reject(requestError);
        }
    );

    return attachAPI<T>(client, requestConfig.apis);
}
